import { useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * Hook to manage session security:
 * - Marks session as active in sessionStorage
 * - Clears session when browser tab/window is closed
 */
export function useSessionSecurity() {
  const markSessionActive = useCallback(() => {
    sessionStorage.setItem('sessionActive', 'true');
  }, []);

  const isSessionActive = useCallback(() => {
    return sessionStorage.getItem('sessionActive') === 'true';
  }, []);

  const clearSession = useCallback(async () => {
    sessionStorage.removeItem('sessionActive');
    localStorage.removeItem('currentSessionId');
    await supabase.auth.signOut();
  }, []);

  useEffect(() => {
    // Check if this is a new tab/window (no session marker)
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session && !isSessionActive()) {
        // User has a session but this is a new tab - they need to re-login
        // This enforces the "close tab = logout" behavior
        await clearSession();
      } else if (session) {
        // Session exists and tab is active, mark it
        markSessionActive();
      }
    };

    checkSession();

    // Handle beforeunload - but we can't reliably logout here
    // Instead we rely on sessionStorage being cleared when tab closes
    const handleBeforeUnload = () => {
      // We don't remove sessionActive here because:
      // 1. beforeunload is unreliable
      // 2. We want refresh to work
      // 3. sessionStorage is tab-specific and auto-clears on close
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isSessionActive, markSessionActive, clearSession]);

  return {
    markSessionActive,
    isSessionActive,
    clearSession,
  };
}
